import { useColorModeValue } from "@chakra-ui/color-mode";
import { useTheme, useToken } from "@chakra-ui/react";
import { Option as IntentsifyOption } from "@intentsify/types";
import { GroupBase, Props } from "react-select";
import { byLook } from "theme/look";

export const useSimpleSelectStyles = <
  Option extends IntentsifyOption,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
  CompiledProps extends Props<Option, IsMulti, Group> = Props<
    Option,
    IsMulti,
    Group
  >,
>() => {
  const chakraTheme = useTheme();
  const placeholderColor = useColorModeValue("gray.500", "#ffffff1d");
  const inputTextColor = useColorModeValue("gray.800", "whiteAlpha.900");
  const singleSelectColor = useColorModeValue("gray.200", "gray.400");
  const simpleControlBg = useColorModeValue("whiteAlpha.200", "whiteAlpha.200");

  // react-select styles are plain CSS, so Chakra tokens must be resolved.
  const [
    brandPlaceholderLight,
    brandPlaceholderDark,
    brandTextLight,
    brandTextDark,
  ] = useToken("colors", [
    "gray.500",
    "whiteAlpha.500",
    "gray.800",
    "gray.100",
  ]);
  const brandPlaceholderColor = useColorModeValue(
    brandPlaceholderLight,
    brandPlaceholderDark,
  );
  const brandTextColor = useColorModeValue(brandTextLight, brandTextDark);

  return {
    placeholder: (base) => {
      return {
        ...base,
        color: byLook(brandPlaceholderColor, placeholderColor),
        fontSize: chakraTheme.fontSizes.sm,
        fontWeight: "normal",
        whiteSpace: "nowrap",
      };
    },
    menu: (base) => {
      return {
        ...base,
        backgroundColor: "transparent",
        boxShadow: "none",
        zIndex: 3,
      };
    },
    input: (base) => {
      return {
        ...base,
        paddingTop: 0,
        paddingBottom: 0,
        color: byLook(brandTextColor, inputTextColor),
      };
    },
    control: (base) => {
      return { ...base, backgroundColor: simpleControlBg };
    },
    indicatorSeparator: (base) => {
      return { ...base, opacity: 0 };
    },
    valueContainer: (base) => {
      return { ...base, padding: "0 0.7rem", minHeight: "1.9rem" };
    },
    singleValue: (base) => {
      return {
        ...base,
        color: byLook(brandTextColor, singleSelectColor),
        fontSize: chakraTheme.fontSizes.sm,
      };
    },
  } satisfies CompiledProps["styles"];
};
